import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Treatment } from '../../../types/admin';
import { DatePicker } from '../../../components/DatePicker';
import { Button } from '../../../components/ui';
import {
  useToothSpacingSchedules,
  useCreateToothSpacingSchedule,
  useDeleteToothSpacingSchedule,
} from '../hooks/useToothSpacing';
import styles from './ToothSpacingSchedulePanel.module.scss';

type ToothSpacingSchedulePanelProps = {
  readonly patientId: string;
  readonly treatments: readonly Treatment[];
};

const INTERVAL_OPTIONS = [7, 10, 14, 21, 30] as const;

type EndMode = 'date' | 'repetitions';

function computeEndDate(
  startDate: string,
  intervalDays: number,
  repetitions: number,
): string {
  const [y, m, d] = startDate.split('-').map(Number);
  if (!y || !m || !d || repetitions < 1) return '';
  const end = new Date(
    Date.UTC(y, m - 1, d + intervalDays * (repetitions - 1)),
  );
  if (Number.isNaN(end.getTime())) return '';
  return end.toISOString().slice(0, 10);
}

function formatDate(value: string, locale: string): string {
  return new Date(`${value}T00:00:00`).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function ToothSpacingSchedulePanel({
  patientId,
  treatments,
}: ToothSpacingSchedulePanelProps) {
  const { t, i18n } = useTranslation();
  const [selectedTreatmentId, setSelectedTreatmentId] = useState(
    treatments[0]?.id ?? '',
  );
  const [intervalDays, setIntervalDays] = useState(14);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endMode, setEndMode] = useState<EndMode>('date');
  const [repetitions, setRepetitions] = useState(10);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const schedulesQuery = useToothSpacingSchedules(
    patientId,
    selectedTreatmentId,
  );
  const createMutation = useCreateToothSpacingSchedule(patientId);
  const deleteMutation = useDeleteToothSpacingSchedule(patientId);

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedTreatmentId || !startDate) return;

    const resolvedEndDate =
      endMode === 'repetitions'
        ? computeEndDate(startDate, intervalDays, repetitions)
        : endDate;

    createMutation.mutate({
      treatment_id: selectedTreatmentId,
      interval_days: intervalDays,
      start_date: startDate,
      end_date: resolvedEndDate || null,
    });
  }

  const schedules = schedulesQuery.data ?? [];

  useEffect(() => {
    if (!schedulesQuery.isLoading && schedules.length === 0) {
      setIsCreateOpen(true);
    }
  }, [schedules.length, schedulesQuery.isLoading]);

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.headerCopy}>
          <h4 className={styles.title}>
            {t('clientDetail.toothSpacingUi.schedule.title')}
          </h4>
          <p className={styles.subtitle}>
            {t('clientDetail.toothSpacingUi.schedule.subtitle')}
          </p>
        </div>
        {!schedulesQuery.isLoading && (
          <span className={styles.countBadge}>{schedules.length}</span>
        )}
      </div>

      {treatments.length > 1 && (
        <label className={`${styles.field} ${styles.treatmentField}`}>
          <span>{t('clientDetail.toothSpacingUi.schedule.treatment')}</span>
          <select
            value={selectedTreatmentId}
            onChange={(e) => setSelectedTreatmentId(e.target.value)}
          >
            {treatments.map((t) => (
              <option key={t.id} value={t.id}>
                {t.diagnosis_titles?.length
                  ? t.diagnosis_titles.join(', ')
                  : t.diagnosis_title || t.id}
              </option>
            ))}
          </select>
        </label>
      )}

      {schedulesQuery.isLoading ? (
        <div
          className={styles.loadingState}
          aria-label={t('clientDetail.toothSpacingUi.schedule.loading')}
        >
          <span />
          <span />
        </div>
      ) : schedulesQuery.isError ? (
        <p className={styles.errorState} role="alert">
          {t('clientDetail.toothSpacingUi.schedule.loadFailed')}
        </p>
      ) : schedules.length > 0 ? (
        <div className={styles.scheduleList}>
          {schedules.map((schedule) => (
            <div key={schedule.id} className={styles.scheduleItem}>
              <div className={styles.scheduleInfo}>
                <div className={styles.scheduleTopline}>
                  <strong>
                    {t('clientDetail.toothSpacingUi.everyNDays', {
                      days: schedule.interval_days,
                    })}
                  </strong>
                  <span
                    className={`${styles.badge} ${schedule.is_active ? styles.active : styles.inactive}`}
                  >
                    {schedule.is_active
                      ? t('clientDetail.toothSpacingUi.status.active')
                      : t('clientDetail.toothSpacingUi.status.inactive')}
                  </span>
                </div>
                <span className={styles.dateRange}>
                  {formatDate(schedule.start_date, i18n.language)}
                  <span aria-hidden="true">→</span>
                  {schedule.end_date
                    ? formatDate(schedule.end_date, i18n.language)
                    : t('clientDetail.toothSpacingUi.schedule.noEndDate')}
                </span>
              </div>
              <div className={styles.scheduleActions}>
                {schedule.is_active && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => deleteMutation.mutate(schedule.id)}
                    disabled={deleteMutation.isPending}
                  >
                    {t('clientDetail.toothSpacingUi.schedule.deactivate')}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className={styles.empty}>
          {t('clientDetail.toothSpacingUi.schedule.empty')}
        </p>
      )}

      <details
        className={styles.createDisclosure}
        open={isCreateOpen}
        onToggle={(event) => setIsCreateOpen(event.currentTarget.open)}
      >
        <summary>
          <span className={styles.summaryIcon} aria-hidden="true">
            +
          </span>
          <span>
            <strong>{t('clientDetail.toothSpacingUi.schedule.newTitle')}</strong>
            <small>{t('clientDetail.toothSpacingUi.schedule.newSubtitle')}</small>
          </span>
        </summary>
        <div className={styles.createForm}>
          <form onSubmit={handleCreate}>
            <div className={styles.formGrid}>
              <label className={styles.field}>
                <span>{t('clientDetail.toothSpacingUi.schedule.interval')}</span>
                <select
                  value={intervalDays}
                  onChange={(e) => setIntervalDays(Number(e.target.value))}
                >
                  {INTERVAL_OPTIONS.map((days) => (
                    <option key={days} value={days}>
                      {t('clientDetail.toothSpacingUi.everyNDays', { days })}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.field}>
                <span>{t('clientDetail.toothSpacingUi.schedule.startDate')}</span>
                <DatePicker
                  value={startDate}
                  onChange={setStartDate}
                  placeholder={t(
                    'clientDetail.toothSpacingUi.schedule.datePlaceholder',
                  )}
                  required
                />
              </label>
              <label className={styles.field}>
                <span>{t('clientDetail.toothSpacingUi.schedule.endMode')}</span>
                <select
                  value={endMode}
                  onChange={(e) => setEndMode(e.target.value as EndMode)}
                >
                  <option value="date">
                    {t('clientDetail.toothSpacingUi.schedule.endByDate')}
                  </option>
                  <option value="repetitions">
                    {t('clientDetail.toothSpacingUi.schedule.endByRepetitions')}
                  </option>
                </select>
              </label>
              {endMode === 'date' ? (
                <label className={styles.field}>
                  <span>
                    {t('clientDetail.toothSpacingUi.schedule.endDateOptional')}
                  </span>
                  <DatePicker
                    value={endDate}
                    onChange={setEndDate}
                    placeholder={t(
                      'clientDetail.toothSpacingUi.schedule.datePlaceholder',
                    )}
                  />
                </label>
              ) : (
                <label className={styles.field}>
                  <span>
                    {t('clientDetail.toothSpacingUi.schedule.repetitions')}
                  </span>
                  <input
                    type="number"
                    min={1}
                    max={365}
                    value={repetitions}
                    onChange={(e) =>
                      setRepetitions(Math.max(1, Number(e.target.value) || 1))
                    }
                  />
                </label>
              )}
            </div>
            <Button
              type="submit"
              variant="primary"
              size="md"
              disabled={createMutation.isPending || !startDate}
            >
              {createMutation.isPending
                ? t('clientDetail.toothSpacingUi.schedule.creating')
                : t('clientDetail.toothSpacingUi.schedule.create')}
            </Button>
            {createMutation.isError && (
              <p className={styles.errorState} role="alert">
                {t('clientDetail.toothSpacingUi.schedule.createFailed')}
              </p>
            )}
          </form>
        </div>
      </details>
    </div>
  );
}
